import { config } from "@repo/config";
import {
	AGENCY_CORE_CITATION_REWRITES_PER_MONTH,
	AGENCY_CORE_INCLUDED_PROMPTS,
	AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH,
	AI_REPORT_CREDITS,
	BRAND_CITATION_REWRITES_PER_MONTH,
	BRAND_INCLUDED_PROMPTS,
	BRAND_SITE_AUDIT_PAGES_PER_MONTH,
	getEntitlements,
} from "@repo/payments/client";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

type ProductReferenceId = keyof (typeof config)["payments"]["plans"];

const BRAND_MONTHLY_CREDITS = config.payments.plans.brand.credits;
const AGENCY_MONTHLY_CREDITS =
	config.payments.plans.agencyCoreUsageBased.credits;

export interface PlanDisplayData {
	title: string;
	description: ReactNode;
	/**
	 * Flat feature list. For plans with grouped features this is the union of
	 * `includedFeatures` + `creditFeatures` (used by ActivePlan and other
	 * compact displays that render a single list).
	 */
	features: ReactNode[];
	/** Monthly allowances included with the plan ("Included every month"). */
	includedFeatures?: ReactNode[];
	/** Outcomes the plan's credit bundle covers ("Your N credits also cover"). */
	creditFeatures?: ReactNode[];
	/**
	 * October 2026 (v2) cards: the monthly allowance rows (prompts, credits,
	 * audit pages, rewrites), read from the entitlements table.
	 */
	allowances?: ReactNode[];
	/** v2 cards: heading above `outcomeFeatures`. */
	featuresHeading?: string;
	/** v2 cards: outcome-led bullets beyond the allowance rows. */
	outcomeFeatures?: ReactNode[];
}

type V2PlanDataKey = "brandV2" | "agencyCoreV2" | "agencyPro";

export function usePlanData() {
	const t = useTranslations();

	// First line of every plan's list: anchors down to the shared
	// "All plans include" section on the pricing page (id="all-plans-include").
	// Where that section isn't rendered (inline/onboarding mode) the anchor is
	// simply a no-op.
	const freeFeaturesItem: ReactNode = t.rich(
		"pricing.planGroups.freeFeatures",
		{
			link: (chunks) => (
				<a
					href="#all-plans-include"
					className="font-medium text-primary underline underline-offset-2"
				>
					{chunks}
				</a>
			),
		},
	);

	const brandIncluded: ReactNode[] = [
		freeFeaturesItem,
		t("pricing.products.brand.included.trackedPrompts", {
			prompts: BRAND_INCLUDED_PROMPTS,
		}),
		t("pricing.products.brand.included.siteAudit", {
			pages: BRAND_SITE_AUDIT_PAGES_PER_MONTH.toLocaleString(),
		}),
		t("pricing.products.brand.included.citationRewrites", {
			rewrites: BRAND_CITATION_REWRITES_PER_MONTH,
		}),
		t("pricing.products.brand.included.competitorBenchmarking"),
		t("pricing.products.brand.included.multiProperty"),
		// SSO is on every subscription plan, not an Enterprise up-sell.
		t("pricing.products.brand.included.sso"),
	];
	const brandCreditUses: ReactNode[] = [
		t("pricing.products.brand.creditUses.reports", {
			reports: Math.floor(BRAND_MONTHLY_CREDITS / AI_REPORT_CREDITS),
			cost: AI_REPORT_CREDITS,
		}),
		t("pricing.products.brand.creditUses.mixAndMatch"),
	];

	const agencyIncluded: ReactNode[] = [
		freeFeaturesItem,
		t("pricing.products.agencyCore.included.everythingInBrand"),
		t("pricing.products.agencyCore.included.trackedPrompts", {
			prompts: AGENCY_CORE_INCLUDED_PROMPTS,
		}),
		t("pricing.products.agencyCore.included.siteAudit", {
			pages: AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH.toLocaleString(),
		}),
		t("pricing.products.agencyCore.included.citationRewrites", {
			rewrites: AGENCY_CORE_CITATION_REWRITES_PER_MONTH,
		}),
		t("pricing.products.agencyCore.included.whiteLabel"),
		t("pricing.products.agencyCore.included.clientPortal"),
		t("pricing.products.agencyCore.included.advancedReporting"),
		t("pricing.products.agencyCore.included.sso"),
		t("pricing.products.agencyCore.included.prioritySupport"),
	];
	const agencyCreditUses: ReactNode[] = [
		t("pricing.products.agencyCore.creditUses.reports", {
			reports: Math.floor(AGENCY_MONTHLY_CREDITS / AI_REPORT_CREDITS),
			cost: AI_REPORT_CREDITS,
		}),
		t("pricing.products.agencyCore.creditUses.mixAndMatch"),
	];

	function v2PlanData(
		key: V2PlanDataKey,
		outcomeFeatures: ReactNode[],
	): PlanDisplayData {
		const e = getEntitlements(key);
		const allowances: ReactNode[] = [
			t("pricing.v2.allowances.prompts", {
				count: e.trackedPrompts.toLocaleString("en-US"),
			}),
			t("pricing.v2.allowances.credits", {
				count: e.monthlyCredits.toLocaleString("en-US"),
			}),
			t("pricing.v2.allowances.auditPages", {
				count: e.siteAuditPages.toLocaleString("en-US"),
			}),
			t("pricing.v2.allowances.citations", {
				rewrites: e.citationRewrites.toLocaleString("en-US"),
				pages: e.citationScoringPages.toLocaleString("en-US"),
			}),
		];
		return {
			title: t(`pricing.products.${key}.title`),
			description: t(`pricing.products.${key}.description`),
			features: [...allowances, ...outcomeFeatures],
			allowances,
			featuresHeading: t(`pricing.products.${key}.featuresHeading`),
			outcomeFeatures,
		};
	}

	const planData: Record<ProductReferenceId, PlanDisplayData> = {
		lifetime: {
			title: t("pricing.products.lifetime.title"),
			description: t("pricing.products.lifetime.description"),
			features: [],
		},
		enterprise: {
			title: t("pricing.products.enterprise.title"),
			description: t("pricing.products.enterprise.description"),
			features: [
				t("pricing.products.enterprise.features.highVolumeDiscount"),
				t("pricing.products.enterprise.features.customImplementation"),
				t("pricing.products.enterprise.features.dedicatedSupport"),
				t("pricing.products.enterprise.features.sla"),
			],
		},
		aiReportUnlock: {
			title: t("pricing.products.aiReportUnlock.title"),
			description: t("pricing.products.aiReportUnlock.description"),
			features: [
				t("pricing.products.aiReportUnlock.features.fullReport"),
				t(
					"pricing.products.aiReportUnlock.features.implementationSteps",
				),
				t("pricing.products.aiReportUnlock.features.brandingAnalysis"),
				t(
					"pricing.products.aiReportUnlock.features.competitorInsights",
				),
			],
		},
		// Legacy bundled-credit agency plan (hidden from new signups)
		agencyCore: {
			title: t("pricing.products.agencyCore.title"),
			description: t("pricing.products.agencyCore.description"),
			features: [
				t("pricing.products.agencyCore.features.includedPrompts", {
					prompts: AGENCY_CORE_INCLUDED_PROMPTS,
				}),
				t("pricing.products.agencyCore.features.siteAudit", {
					pages: AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH.toLocaleString(),
				}),
				t("pricing.products.agencyCore.features.multiProperty"),
				t("pricing.products.agencyCore.features.advancedReporting"),
				t("pricing.products.agencyCore.features.prioritySupport"),
			],
		},
		brand: {
			title: t("pricing.products.brand.title"),
			description: t("pricing.products.brand.description"),
			features: [...brandIncluded, ...brandCreditUses],
			includedFeatures: brandIncluded,
			creditFeatures: brandCreditUses,
		},
		agencyCoreUsageBased: {
			title: t("pricing.products.agencyCore.title"),
			description: t("pricing.products.agencyCore.description"),
			features: [...agencyIncluded, ...agencyCreditUses],
			includedFeatures: agencyIncluded,
			creditFeatures: agencyCreditUses,
		},
		// October 2026 (v2) plans. Allowance numbers come from the
		// entitlements table; bullets lead with outcomes.
		brandV2: v2PlanData("brandV2", [
			t("pricing.products.brandV2.features.seeCompetitors"),
			t("pricing.products.brandV2.features.trustedSources"),
			t("pricing.products.brandV2.features.citationOptimizer"),
			t("pricing.products.brandV2.features.siteAudits"),
		]),
		agencyCoreV2: v2PlanData("agencyCoreV2", [
			t("pricing.products.agencyCoreV2.features.brandedReports"),
			t("pricing.products.agencyCoreV2.features.clientPortal"),
			t("pricing.products.agencyCoreV2.features.placementScores"),
			t("pricing.products.agencyCoreV2.features.mediaLists"),
			t("pricing.products.agencyCoreV2.features.prospecting"),
		]),
		agencyPro: v2PlanData("agencyPro", [
			t("pricing.products.agencyPro.features.everythingInCore"),
			t("pricing.products.agencyPro.features.moreClients"),
		]),
		paygCredits: {
			title: t("pricing.products.paygCredits.title"),
			description: t("pricing.products.paygCredits.description"),
			features: [
				freeFeaturesItem,
				t("pricing.products.paygCredits.features.payAsYouGo"),
				t("pricing.products.paygCredits.features.minPurchase"),
				t("pricing.products.paygCredits.features.unlimitedProperties"),
				t("pricing.products.paygCredits.features.promptTracking"),
				t("pricing.products.paygCredits.features.flexibleUse"),
			],
		},
	};

	return { planData };
}
